import axios from "axios";
import { tokenService } from '../utils/token';


export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 20000
});

apiClient.interceptors.request.use(
  (config) => {
    const tokens = tokenService.getToken();
    const accessToken = tokens ? JSON.parse(tokens).accessToken : null;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// apiClient.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     if (error.response?.status === 403) {
//       console.warn('401 Unauthorized – redirecting to login');
//       tokenService.removeToken();
//       window.location.href = '/login';
//     }
//     return Promise.reject(error);
//   }
// );


apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config.url?.includes('/login'); // adjust to your login endpoint
      if (!isLoginRequest) {
        console.warn('401 Unauthorized – redirecting to login');
        tokenService.removeToken();
        window.location.href = '/login';
      } else {
        // Let the login component handle the error (show "Invalid credentials")
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  login: async (credentials) => {
    console.log('🌐 authAPI.login started', credentials);

    try {
      const response = await apiClient.post('/auth/login', credentials);
      console.log('📦 authAPI.login response:', response);

      const { user, tokens } = response.data.data;
      console.log('👤 User from API:', user);
      console.log('🔑 Tokens received');

      return { user, tokens };
    } catch (error) {
      console.error('💥 authAPI.login error:', error);
      console.log('❌ Error response:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 'Login failed. Please try again later.'
      );
    }
  },

  register: async (userData) => {
    try {
      // Replace with your actual registration endpoint
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error.response?.data?.message || 'Registration failed. Please try again.';
    }
  },

  logout: async () => {
    try {
      // Call logout endpoint if your backend has one
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      tokenService.clearAll();
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password request failed:', error);
      throw error.response?.data?.message || 'Password reset request failed.';
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await apiClient.post('/auth/reset-password', {
        token,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password reset failed:', error);
      throw error.response?.data?.message || 'Password reset failed.';
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password change failed:', error);
      throw error.response?.data?.message || 'Password change failed.';
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error.response?.data?.message || 'Failed to fetch user information.';
    }
  }
};

// Customer API functions
export const customerAPI = {
  getDashboardStats: async (role) => {
    try {
      let endpoint = '';

      if (role === 'admin') {
        endpoint = '/admin/dashboard/admin'; // Admin endpoint
      } else {
        endpoint = '/kyc/officer/dashboard'; // Non-admin endpoint
      }

      const response = await apiClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      throw error.response?.data?.message || 'Failed to fetch dashboard statistics.';
    }
  },

  getPendingCustomers: async ({ page, limit, search, ...rest } = {}) => {
    try {
      const queryParams = {
        search: search ?? '',
        ...rest
      };

      // Only add pagination when explicitly required
      if (page !== undefined && limit !== undefined) {
        queryParams.page = page;
        queryParams.limit = limit;
      }

      const response = await apiClient.get('/kyc/officer/pending', {
        params: queryParams
      });

      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Failed to fetch pending customers:', error);
      throw error.response?.data?.message || 'Failed to fetch pending customers.';
    }
  },

  getVerifiedCustomers: async ({ page, limit, search, ...rest } = {}) => {
    try {
      const queryParams = {
        search: search ?? '',
        ...rest
      };

      // Only add pagination when explicitly required
      if (page !== undefined && limit !== undefined) {
        queryParams.page = page;
        queryParams.limit = limit;
      }

      const response = await apiClient.get('/kyc/officer/approved/only', {
        params: queryParams
      });
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Failed to fetch verified customers:', error);
      throw error.response?.data?.message || 'Failed to fetch verified customers.';
    }
  },

  getApprovedOverriddenCustomers: async ({ page, limit, search, ...rest } = {}) => {
    try {
      const queryParams = {
        search: search ?? '',
        ...rest
      };

      // Only add pagination when explicitly required
      if (page !== undefined && limit !== undefined) {
        queryParams.page = page;
        queryParams.limit = limit;
      }

      const response = await apiClient.get('/kyc/officer/approved/with-override', {
        params: queryParams
      });

      console.log('all response: ', response);
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Failed to fetch verified customers:', error);
      throw error.response?.data?.message || 'Failed to fetch verified customers.';
    }
  },

  getRejectedCustomers: async ({ page, limit, search, ...rest } = {}) => {
    try {
      const queryParams = {
        search: search ?? '',
        ...rest
      };

      // Only add pagination when explicitly required
      if (page !== undefined && limit !== undefined) {
        queryParams.page = page;
        queryParams.limit = limit;
      }

      const response = await apiClient.get('/kyc/officer/rejected', {
        params: queryParams
      });
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Failed to fetch rejected customers:', error);
      throw error.response?.data?.message || 'Failed to fetch rejected customers.';
    }
  },

  getAllCustomers: async (params = {}) => {
    try {
      const response = await apiClient.get('/customers', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          status: params.status,
          search: params.search || '',
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      throw error.response?.data?.message || 'Failed to fetch customers.';
    }
  },

  getCustomerById: async (id) => {
    try {
      const response = await apiClient.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch customer ${id}:`, error);
      throw error.response?.data?.message || 'Failed to fetch customer details.';
    }
  },

  verifyCustomer: async (customerId, body) => {
    try {
      console.log('Verifying customer:', customerId, body);

      const { action, comment, approvalType } = body;

      if (action !== 'approve' && action !== 'reject') {
        throw new Error('Invalid action. Must be "approve" or "reject".');
      }

      let response;

      if (action === 'reject') {
        // For reject, typically you want to send the comment as the reason
        response = await apiClient.post(`/kyc/officer/verification/customer/${customerId}/reject`, {
          reason: comment, // Send the actual comment as reason for rejection
          // comment: comment  // Or if your backend expects comment field
        });
      } else if (action === 'approve') {
        response = await apiClient.post(`/kyc/officer/verification/customer/${customerId}/approve`, body);
      }

      return response.data;

    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to verify customer');
      }

      console.error('Customer verification failed:', error);
      throw error.response?.data?.message || 'Customer verification failed.';
    }
  },

  updateCustomerOverride: async (customerId, field, overrideValue) => {
    try {
      const response = await apiClient.patch(`/customers/${customerId}/override`, {
        field,
        overrideValue
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update customer override:', error);
      throw error.response?.data?.message || 'Failed to update customer override.';
    }
  },

  // Batch operations if supported by your backend
  batchVerifyCustomers: async (customerIds, action, comment = '') => {
    try {
      const response = await apiClient.post('/customers/batch-verify', {
        customerIds,
        action,
        comment
      });
      return response.data;
    } catch (error) {
      console.error('Batch verification failed:', error);
      throw error.response?.data?.message || 'Batch verification failed.';
    }
  }
};

// User Management API functions
export const userAPI = {
  getUsers: async () => {
    try {
      const response = await apiClient.get('/admin/users');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error.response?.data?.message || 'Failed to fetch users.';
    }
  },

  createUser: async (userData) => {
    try {
      console.log('Creating user with data:', userData);
      const response = await apiClient.post('/admin/users', userData);
      return response.data;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error.response?.data?.message || 'Failed to create user.';
    }
  },

  updateUser: async (id, userData) => {
    try {
      const response = await apiClient.put(`/admin/users/${id}`, userData);
      console.log('Update user response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update user ${id}:`, error);
      throw error.response?.data?.message || 'Failed to update user.';
    }
  },

  blockUser: async (id, reason) => {
    try {
      const response = await apiClient.post(`/admin/users/${id}/block`, { reason });
      return response.data;
    } catch (error) {
      console.error(`Failed to block user ${id}:`, error);
      throw error.response?.data?.message || 'Failed to block user.';
    }
  },

  resetPassword: async (id, body) => {
    try {
      const response = await apiClient.post(
        `/admin/users/${id}/reset-password`,
        body
      );

      return response.data;

    } catch (error) {
      // Forward backend error properly
      throw error;
    }
  },

  deleteUser: async (id) => {
    try {
      const response = await apiClient.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete user ${id}:`, error);
      throw error.response?.data?.message || 'Failed to delete user.';
    }
  },

  // User roles and permissions
  updateUserRole: async (userId, role) => {
    try {
      const response = await apiClient.patch(`/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error(`Failed to update user role:`, error);
      throw error.response?.data?.message || 'Failed to update user role.';
    }
  },


  getUnauthorizedUsers: () => apiClient.get('/users/unauthorized'),
  authorizeUser: (id, action) =>
    apiClient.post(`/users/${id}/authorize`, { action }),

};

// Activity Log API functions
export const activityAPI = {
  getActivityLogs: async (params = {}) => {
    try {
      const response = await apiClient.get('/activities', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          userId: params.userId,
          actionType: params.actionType,
          startDate: params.startDate,
          endDate: params.endDate,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      throw error.response?.data?.message || 'Failed to fetch activity logs.';
    }
  },

  getCustomerActivities: async (customerId, params = {}) => {
    try {
      const response = await apiClient.get(`/customers/${customerId}/activities`, {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch activities for customer ${customerId}:`, error);
      throw error.response?.data?.message || 'Failed to fetch customer activities.';
    }
  }
};

// File Upload API functions (if needed)
export const fileAPI = {
  uploadDocument: async (customerId, file, documentType) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);

      const response = await apiClient.post(`/customers/${customerId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Document upload failed:', error);
      throw error.response?.data?.message || 'Document upload failed.';
    }
  },

  downloadDocument: async (customerId, documentId) => {
    try {
      const response = await apiClient.get(`/customers/${customerId}/documents/${documentId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Document download failed:', error);
      throw error.response?.data?.message || 'Document download failed.';
    }
  }
};

// Reports API functions
export const reportAPI = {
  generateReport: async (reportType, params = {}) => {
    try {
      const response = await apiClient.post('/reports/generate', {
        reportType,
        ...params
      }, {
        responseType: 'blob' // For file downloads
      });
      return response.data;
    } catch (error) {
      console.error('Report generation failed:', error);
      throw error.response?.data?.message || 'Report generation failed.';
    }
  },

  getReportHistory: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/history', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch report history:', error);
      throw error.response?.data?.message || 'Failed to fetch report history.';
    }
  }
};

// Export all API functions
export default {
  auth: authAPI,
  customer: customerAPI,
  user: userAPI,
  activity: activityAPI,
  file: fileAPI,
  report: reportAPI
};



